import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import type {
  FormFieldOption,
  InsertFormFieldMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetFormConfigurationByParentTypeDocument,
  GetFormCustomisationDocument,
  GetFormFieldOptionsByParentTypeDocument,
  InsertFormFieldDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type InsertFormFieldInput = {
  ParentType: string;
  Type: string;
  Label: string;
  AltLabel?: string;
  Description?: string | null;
  Options: FormFieldOption[];
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Required: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Hidden: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  ReadOnly: boolean;
  DefaultValue?: string | null;
  Conditions?: unknown;
  IsCustomField: true;
};

type OptionField =
  | { _tag: 'StringOption'; Value: string }
  | { _tag: 'AltValueOption'; Value: string; AltValue: string };

type InsertFormFieldTRPCInput = Omit<
  InsertFormFieldInput,
  'ParentType' | 'Options'
> & {
  ParentType: ParentType;
  Options: OptionField[];
};

/**
 * Converts FormFieldOption to the tRPC-compatible option format
 */
const convertOptionsForTRPC = (options: FormFieldOption[]): OptionField[] =>
  options.map((opt) =>
    opt._tag === 'AltValueOption'
      ? {
          _tag: 'AltValueOption' as const,
          Value: opt.Value,
          AltValue: opt.AltValue ?? '',
        }
      : {
          _tag: 'StringOption' as const,
          Value: opt.Value,
        }
  );

export const useInsertFormField = createMutationHook({
  name: 'insertFormField',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.formConfiguration.createFormField.mutationOptions({
      onSuccess,
    }),
  mapInput: (variables: InsertFormFieldInput): InsertFormFieldTRPCInput => ({
    ...variables,
    ParentType: variables.ParentType as ParentType,
    Options: convertOptionsForTRPC(variables.Options),
  }),
  // Invalidate BOTH form-configuration readers. `getByParentTypes` backs
  // EditFieldModal (lean, fields_config only); `getFormCustomisation` backs
  // CustomisableFormDataProvider, which renders the live form
  // (custom_attribute_schema). Omitting the latter leaves an already-open
  // form stale — the new custom field never renders until the form is
  // remounted (RSP-5866).
  invalidate: (trpc) => [
    trpc.frontend.formConfiguration.getByParentTypes.queryKey(),
    trpc.frontend.formConfiguration.getFormCustomisation.queryKey(),
  ],
  mapResponse: (result: { Id: string }): InsertFormFieldMutation => ({
    insertFormField: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertFormFieldDocument,
  graphqlVariables: (variables: InsertFormFieldInput) => ({
    object: {
      Type: variables.Type,
      IsCustomField: variables.IsCustomField,
      DefaultValue: variables.DefaultValue,
      Description: variables.Description,
      Hidden: variables.Hidden,
      Label: variables.Label,
      AltLabel: variables.AltLabel,
      Options: variables.Options,
      ParentType: variables.ParentType,
      ReadOnly: variables.ReadOnly,
      Required: variables.Required,
      Conditions: variables.Conditions,
    },
  }),
  refetchQueries: [
    GetFormConfigurationByParentTypeDocument,
    GetFormFieldOptionsByParentTypeDocument,
    GetFormCustomisationDocument,
  ],
});
